import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Calendar, FileText, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { useAnalysisHistory, AnalysisHistoryItem } from '@/hooks/useAnalysisHistory';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';

interface AnalysisHistoryPanelProps {
  auditId: string;
  moduleType: string;
}

export function AnalysisHistoryPanel({ auditId, moduleType }: AnalysisHistoryPanelProps) {
  const { history, isLoading, error } = useAnalysisHistory(auditId, moduleType);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Analyshistorik</CardTitle>
          <CardDescription>Laddar tidigare analyser...</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (history.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Analyshistorik</CardTitle>
          <CardDescription>Inga tidigare analyser hittades</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  // Beräkna trender
  const getTrend = (current: number, previous: number | undefined) => {
    if (previous === undefined) return 'neutral';
    if (current > previous) return 'up';
    if (current < previous) return 'down';
    return 'neutral';
  };

  const TrendIcon = ({ trend }: { trend: 'up' | 'down' | 'neutral' }) => {
    if (trend === 'up') return <TrendingUp className="h-4 w-4 text-orange-500" />;
    if (trend === 'down') return <TrendingDown className="h-4 w-4 text-green-500" />;
    return <Minus className="h-4 w-4 text-muted-foreground" />;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Analyshistorik</CardTitle>
        <CardDescription>
          Visar de {Math.min(history.length, 5)} senaste analyserna
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {history.map((item, index) => {
          const previousItem = history[index + 1];
          const totalFindingsTrend = getTrend(
            item.criticalCount + item.highCount + item.mediumCount + item.lowCount,
            previousItem ? previousItem.criticalCount + previousItem.highCount + previousItem.mediumCount + previousItem.lowCount : undefined
          );
          const riskScoreTrend = getTrend(item.riskScore, previousItem?.riskScore);

          return (
            <div
              key={item.revisionId}
              className="border rounded-lg p-4 space-y-3 hover:bg-accent/50 transition-colors"
            >
              {/* Header */}
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">{item.fileName || 'Okänd fil'}</span>
                  {index === 0 && (
                    <Badge variant="outline" className="text-xs">
                      Senaste
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                  <Calendar className="h-3 w-3" />
                  {format(new Date(item.createdAt), 'PPP p', { locale: sv })}
                </div>
              </div>

              {/* Statistik */}
              <div className="grid grid-cols-4 gap-3">
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600">{item.criticalCount}</div>
                  <div className="text-xs text-muted-foreground">Kritiska</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-600">{item.highCount}</div>
                  <div className="text-xs text-muted-foreground">Höga</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-yellow-600">{item.mediumCount}</div>
                  <div className="text-xs text-muted-foreground">Medel</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">{item.lowCount}</div>
                  <div className="text-xs text-muted-foreground">Låga</div>
                </div>
              </div>

              {/* Trender */}
              {previousItem && (
                <div className="flex items-center justify-between pt-2 border-t text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">Totalt fynd:</span>
                    <TrendIcon trend={totalFindingsTrend} />
                    <span className="font-medium">
                      {item.criticalCount + item.highCount + item.mediumCount + item.lowCount}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">Risk:</span>
                    <TrendIcon trend={riskScoreTrend} />
                    <span className="font-medium">{item.riskScore.toFixed(1)}</span>
                  </div>
                </div>
              )}

              {/* Info om första analysen (ingen jämförelse) */}
              {!previousItem && index === history.length - 1 && (
                <div className="pt-2 border-t">
                  <div className="text-sm text-muted-foreground flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    Första analysen - ingen jämförelse tillgänglig
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
